import React from 'react';
import { connect } from 'react-redux';
import { toggleValues } from '@department-of-veterans-affairs/platform-site-wide/selectors';
import FEATURE_FLAG_NAMES from '@department-of-veterans-affairs/platform-utilities/featureFlagNames';
import PropTypes from 'prop-types';

import {
  claimAvailable,
  isChampvaProviderClaim,
  withClaimStatusMetaIfEnabled,
  getFailedSubmissionsWithinLast30Days,
} from '~/platform/shared/claims-appeals';
import {
  clearNotification,
  fetchVesEligibility,
  resetVesEligibility,
} from '../actions';
import { cstMultiClaimProvider } from '../selectors';
import ClaimDetailLayout from '../components/ClaimDetailLayout';
import WhatYouNeedToDo from '../components/claim-status-tab/WhatYouNeedToDo';
import ClaimStatusHeader from '../components/ClaimStatusHeader';
import WhatWeAreDoing from '../components/claim-status-tab/WhatWeAreDoing';
import RecentActivity from '../components/claim-status-tab/RecentActivity';
import NextSteps from '../components/claim-status-tab/NextSteps';
import Payments from '../components/claim-status-tab/Payments';
import ClosedClaimAlert from '../components/claim-status-tab/ClosedClaimAlert';
import ChampvaClosedAlert from '../components/claim-status-tab/ChampvaClosedAlert';
import ChampvaRepeatIneligibilityAlert from '../components/claim-status-tab/ChampvaRepeatIneligibilityAlert';
import ChampvaEligibilityCards from '../components/claim-status-tab/ChampvaEligibilityCards';
import ChampvaNextSteps from '../components/claim-status-tab/ChampvaNextSteps';

import {
  isClaimOpen,
  setPageFocus,
  setTabDocumentTitle,
  getType2FocusedIds,
} from '../utils/helpers';
import { setUpPage, isTab } from '../utils/page';

class ClaimStatusPage extends React.Component {
  // Returns true when the VES eligibility fetch has not yet been attempted
  // for the current claim. Extracted to avoid duplicating the guard logic
  // between componentDidMount and componentDidUpdate.
  vesEligibilityNotYetFetched() {
    const { vesEligibility } = this.props;
    return (
      !vesEligibility.loading &&
      vesEligibility.data === null &&
      !vesEligibility.throttled
    );
  }

  componentDidMount() {
    const {
      claim,
      cstMultiClaimProviderEnabled,
      ivcChampvaVesEligibilityOnDemand,
    } = this.props;
    if (!this.props.asTabPanel) {
      // Only set the document title at mount-time if the claim is already available.
      if (claimAvailable(claim)) setTabDocumentTitle(claim, 'Status');

      setTimeout(() => {
        const { lastPage, loading } = this.props;
        // Skip setPageFocus when the Type 2 error alert is about to claim focus
        // at 150ms for new (unseen) failure IDs. The alert fires first and has
        // already marked those IDs so getType2FocusedIds() agrees with it here,
        // eliminating the fragile 50ms margin between the two competing timers.
        const failedSubs = getFailedSubmissionsWithinLast30Days(
          this.props.claim?.attributes?.evidenceSubmissions,
        );
        const alreadySeen = getType2FocusedIds();
        const alertWillClaimFocus = failedSubs.some(
          s => !alreadySeen.has(s.id),
        );
        if (!alertWillClaimFocus) {
          setPageFocus(lastPage, loading);
        }
      }, 100);
    }

    // If the claim is already in Redux when this component mounts (e.g. the
    // user navigated away and came back), fire the VES fetch immediately.
    if (
      ivcChampvaVesEligibilityOnDemand &&
      claimAvailable(claim) &&
      isChampvaProviderClaim(claim) &&
      this.vesEligibilityNotYetFetched()
    ) {
      const provider = cstMultiClaimProviderEnabled
        ? claim.attributes?.provider
        : null;
      this.props.fetchVesEligibility(claim.id, provider);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      claim,
      cstMultiClaimProviderEnabled,
      ivcChampvaVesEligibilityOnDemand,
      lastPage,
      loading,
    } = this.props;

    if (!this.props.asTabPanel) {
      if (!loading && prevProps.loading && !isTab(lastPage)) {
        setUpPage(false, 'h1');
      }
      // Set the document title when loading completes.
      //   If loading was successful it will display a title specific to the claim.
      //   Otherwise it will display a default title of "Status of Your Claim".
      if (loading !== prevProps.loading) {
        setTabDocumentTitle(claim, 'Status');
      }
    }

    // Fire VES eligibility fetch once when the claim transitions from
    // unavailable to available. Guards prevent re-firing on subsequent renders
    // (e.g. tab switches) within the same Redux session.
    const claimJustBecameAvailable =
      !claimAvailable(prevProps.claim) && claimAvailable(claim);

    // Reset VES eligibility state when the user navigates to a different claim
    // so the fetch fires fresh for each claim rather than reusing stale data
    // from the previous one.
    const claimIdChanged =
      claimAvailable(prevProps.claim) &&
      claimAvailable(claim) &&
      prevProps.claim.id !== claim.id;
    if (claimIdChanged) {
      this.props.resetVesEligibility();
      // The new claim is already available, so claimJustBecameAvailable will
      // be false. Fire the fetch immediately rather than waiting for another
      // update cycle that will never come.
      if (ivcChampvaVesEligibilityOnDemand && isChampvaProviderClaim(claim)) {
        const provider = cstMultiClaimProviderEnabled
          ? claim.attributes?.provider
          : null;
        this.props.fetchVesEligibility(claim.id, provider);
      }
      return;
    }

    if (
      ivcChampvaVesEligibilityOnDemand &&
      claimJustBecameAvailable &&
      isChampvaProviderClaim(claim) &&
      this.vesEligibilityNotYetFetched()
    ) {
      const provider = cstMultiClaimProviderEnabled
        ? claim.attributes?.provider
        : null;
      this.props.fetchVesEligibility(claim.id, provider);
    }
  }

  getPageContent() {
    const {
      claim,
      closedTrackedItemsEnabled,
      cstChampvaCustomContentEnabled,
      ivcChampvaVesEligibilityOnDemand,
    } = this.props;

    const displayClaim = withClaimStatusMetaIfEnabled(
      claim,
      cstChampvaCustomContentEnabled,
    );

    // Return null if the claim/ claim.attributes dont exist
    if (!claimAvailable(displayClaim)) {
      return null;
    }

    const { closeDate, decisionLetterSent, status, claimStatusMeta } =
      displayClaim.attributes;
    const isOpen = isClaimOpen(status, closeDate);
    const isChampvaClaim =
      isChampvaProviderClaim(displayClaim) &&
      cstChampvaCustomContentEnabled &&
      !!claimStatusMeta?.closedAlert;

    // Eligibility cards — show in CHAMPVA open and closed paths when both
    // flags are on and the BE has returned applicant eligibility data.
    // cstChampvaApplicants is a top-level attribute on the claim, not nested
    // under claimStatusMeta.
    const champvaApplicants =
      isChampvaProviderClaim(displayClaim) &&
      cstChampvaCustomContentEnabled &&
      ivcChampvaVesEligibilityOnDemand
        ? displayClaim.attributes?.cstChampvaApplicants || []
        : [];
    const repeatIneligibilityAlert =
      isChampvaProviderClaim(displayClaim) &&
      cstChampvaCustomContentEnabled &&
      ivcChampvaVesEligibilityOnDemand
        ? displayClaim.attributes?.repeatIneligibilityAlert || null
        : null;

    const repeatIneligibilityCard =
      claimStatusMeta?.repeatIneligibilityCard || null;

    // #154551. Enrolled-card copy from the BE config. Passed to both card renders below:
    // an applicant can be decided eligible while the application itself is still open, so
    // ENROLLED cards appear on the open branch too.
    const enrolledCard = claimStatusMeta?.enrolledCard || null;

    return (
      <div className="claim-status">
        <ClaimStatusHeader claim={displayClaim} />
        {isOpen ? (
          <>
            <WhatYouNeedToDo
              claim={displayClaim}
              closedTrackedItemsEnabled={closedTrackedItemsEnabled}
            />
            <WhatWeAreDoing claim={displayClaim} />
            <ChampvaEligibilityCards
              applicants={champvaApplicants}
              enrolledCard={enrolledCard}
              useVaTabs={this.props.useVaTabs}
            />
          </>
        ) : (
          <>
            {isChampvaClaim ? (
              <>
                {repeatIneligibilityAlert ? (
                  <ChampvaRepeatIneligibilityAlert
                    alert={repeatIneligibilityAlert}
                    linkText={repeatIneligibilityCard?.linkText}
                  />
                ) : (
                  <ChampvaClosedAlert claim={displayClaim} />
                )}
                <ChampvaEligibilityCards
                  applicants={champvaApplicants}
                  repeatIneligibilityCard={repeatIneligibilityCard}
                  enrolledCard={enrolledCard}
                  useVaTabs={this.props.useVaTabs}
                />
                <ChampvaNextSteps claim={displayClaim} />
              </>
            ) : (
              <>
                <ClosedClaimAlert
                  closeDate={closeDate}
                  decisionLetterSent={decisionLetterSent}
                />
                <Payments />
                <NextSteps />
              </>
            )}
          </>
        )}
        <RecentActivity
          claim={displayClaim}
          closedTrackedItemsEnabled={closedTrackedItemsEnabled}
        />
      </div>
    );
  }

  render() {
    const { claim, cstChampvaCustomContentEnabled, loading, message } =
      this.props;
    const displayClaim = withClaimStatusMetaIfEnabled(
      claim,
      cstChampvaCustomContentEnabled,
    );

    let content = null;
    if (!loading) {
      content = this.getPageContent();
    }

    if (this.props.asTabPanel) {
      return content;
    }

    return (
      <ClaimDetailLayout
        claim={displayClaim}
        clearNotification={this.props.clearNotification}
        currentTab="Status"
        loading={loading}
        message={message}
      >
        {content}
      </ClaimDetailLayout>
    );
  }
}

function mapStateToProps(state) {
  const claimsState = state.disability.status;

  return {
    claim: claimsState.claimDetail.detail,
    closedTrackedItemsEnabled:
      state.featureToggles?.cst_surface_closed_tracked_items || false,
    cstChampvaCustomContentEnabled:
      state.featureToggles?.cst_champva_custom_content || false,
    cstMultiClaimProviderEnabled: cstMultiClaimProvider(state) || false,
    ivcChampvaVesEligibilityOnDemand:
      state.featureToggles?.ivc_champva_ves_eligibility_on_demand || false,
    lastPage: claimsState.routing.lastPage,
    loading: claimsState.claimDetail.loading,
    message: claimsState.notifications.message,
    useVaTabs: toggleValues(state)[FEATURE_FLAG_NAMES.cstClaimDetailsVaTabs],
    vesEligibility: claimsState.vesEligibility || {
      loading: false,
      data: null,
      throttled: false,
    },
  };
}

const mapDispatchToProps = {
  clearNotification,
  fetchVesEligibility,
  resetVesEligibility,
};

ClaimStatusPage.defaultProps = {
  vesEligibility: { loading: false, data: null, throttled: false },
};

ClaimStatusPage.propTypes = {
  asTabPanel: PropTypes.bool,
  claim: PropTypes.object,
  clearNotification: PropTypes.func,
  closedTrackedItemsEnabled: PropTypes.bool,
  cstChampvaCustomContentEnabled: PropTypes.bool,
  cstMultiClaimProviderEnabled: PropTypes.bool,
  fetchVesEligibility: PropTypes.func,
  ivcChampvaVesEligibilityOnDemand: PropTypes.bool,
  lastPage: PropTypes.string,
  loading: PropTypes.bool,
  message: PropTypes.object,
  resetVesEligibility: PropTypes.func,
  useVaTabs: PropTypes.bool,
  vesEligibility: PropTypes.shape({
    data: PropTypes.object,
    loading: PropTypes.bool,
    throttled: PropTypes.bool,
  }),
};

export default connect(mapStateToProps, mapDispatchToProps)(ClaimStatusPage);

export { ClaimStatusPage };
